import { graphql } from "react-relay";

import { EventSummaryRoom } from "./EventSummaryRoom";
import { EventSummaryRecurrence } from "./EventSummaryRecurrence";
import { EventSummaryTime } from "./EventSummaryTime";
import { EventSummaryAutopilot } from "./EventSummaryAutopilot";
import { EventSummaryColorCategory } from "@components/event-summary-color-category";

export const eventSummaryFragments = {
  user: graphql`
    fragment EventSummary_user on User {
      ...EventSummaryColorCategory_user
      ...AutopilotFormSaveOnEdit_user
      emails
    }
  `,
  eventParent: graphql`
    fragment EventSummary_eventParent on EventParent {
      ...AutopilotFormSaveOnEdit_eventParent
    }
  `,
  org: graphql`
    fragment EventSummary_org on Org
    @argumentDefinitions(calendarIds: { type: "[String]!", defaultValue: [] }) {
      ...EventSummaryAutopilot_org
      ...EventSummaryColorCategory_org
      ...EventSummaryRecurrence_org
      ...EventSummaryTime_org
      ...EventTimeAndRecurrence_org
      ...AutopilotFormSaveOnEdit_org @arguments(calendarIds: $calendarIds)
      id
      experiments
      primaryOrgCalendar
      nextDefragTime {
        ... on DefragTime {
          millis
        }
      }
      shadowCalendarSettingsErrorable {
        __typename
        ... on ShadowCalendarSettings {
          focusTimeSyncAll
          focusTimeIdealMinutesPerWeek
        }
        ... on GraphEntityError {
          statusCode
          message
        }
      }
      personsByCalendarIdsErrorable(calendarIds: $calendarIds) {
        __typename
        ... on PersonList {
          list {
            id
            emails
            primaryEmail
            profile {
              givenName
              familyName
              externalImageUrl
            }
            isYou
          }
        }
        ... on GraphEntityError {
          message
        }
      }
      userTeams {
        __typename
        ... on TeamList {
          list {
            id
            teamId
            teamCalendarId
            teamName
            createdBy {
              value
            }
            teamMembers {
              person {
                __typename
                id
                personId
                primaryCalendarId
                isYou
                userId
                profile {
                  givenName
                  familyName
                  externalImageUrl
                }
              }
            }
            invitedMembers {
              person {
                __typename
                id
                personId
                primaryCalendarId
                isYou
                userId
                profile {
                  givenName
                  familyName
                  externalImageUrl
                }
              }
            }
          }
        }
      }
      userGoals(first: 1000000) {
        edges {
          node {
            id
            name
            options {
              specificOptions {
                ... on GoalWithinWeekOptions {
                  maxMinutesPerBlock
                }
                ... on GoalWithinDayOptions {
                  minimumDuration
                  idealDuration
                  minTimeSlot
                  maxTimeSlot
                }
              }
            }
          }
        }
      }
    }
  `,
  event: graphql`
    fragment EventSummary_event on Event @argumentDefinitions(calendarId: { type: "String!" }) {
      ...EventSummaryColorCategory_event
      ...EventSummaryRecurrence_event
      ...EventSummaryTime_event
      ...EventSummaryAutopilot_event @arguments(calendarId: $calendarId)
      ...EventTimeAndRecurrence_event
      ...AutopilotFormSaveOnEdit_event @arguments(calendarId: $calendarId)
      title
      isAllDay
      description
      descriptionOmitted
      locked
      isAllDay
      isClockwiseEvent
      createdMillis
      updatedMillis
      hangoutLink
      scrubbedEvent
      organizer {
        urnValue
      }
      eventKey {
        externalEventId
      }
      annotatedEvent {
        orgTags {
          tag
          state {
            subjectType
            subjectValue
            value
            active
          }
        }
      }
      attendees {
        ...EventSummaryRoom_attendees
        organizer
        displayName
        urnValue
      }
      startTime {
        millis
      }
      endTime {
        millis
      }
      recurrenceRules {
        firstInstanceStartTime {
          type
          dateOrDateTime
          timeZone
          millis
          offsetMinutes
        }
        timeZone
        interval {
          interval
          byDay
          bySetPos
          freqType
          count
          untilTime {
            type
            dateOrDateTime
            timeZone
            millis
            offsetMinutes
          }
        }
      }
    }
  `,
  smartHoldEventInfo: graphql`
    fragment EventSummary_smartHoldEventInfo on SmartHoldEventInfo {
      isPreTravelTime
      isPostTravelTime
      originEvent {
        title
        eventKey {
          externalEventId
        }
      }
    }
  `,
};

export const refetchQuery = graphql`
  query EventSummaryQuery($orgRelayId: ID!, $calendarIds: [String]!) {
    org: node(id: $orgRelayId) {
      ... on Org {
        ...EventSummary_org @arguments(calendarIds: $calendarIds)
      }
    }
  }
`;
